import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import {
  searchTrainingPlans,
  createTrainingPlan,
  updateTrainingPlanById,
  archiveMultipleTrainingPlans,
  restoreMultipleTrainingPlans,
  createTrainingPlanVariant,
  duplicateTrainingPlan
} from 'API/training-plan'
import {
  ITrainingPlan,
  ITrainingPlanWithRelations,
  ITrainingPlanSearchFilterDto,
  ICreateTrainingPlanDto
} from 'interfaces/training-plan'

class TrainingPlanStore {
  rootStore: RootStore
  trainingPlans: ITrainingPlanWithRelations[] = []
  numberOfTrainingPlans: number = 0
  isFetchingList: boolean = false
  isManageModeInList: boolean = false
  isCreatingVariant: boolean = false
  isDuplicatingPlan: boolean = false

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  async searchTrainingPlans(filterDto: ITrainingPlanSearchFilterDto, orgId?: number): Promise<void> {
    this.isFetchingList = true
    try {
      const { results, totalCount } = await searchTrainingPlans(filterDto, orgId)
      this.trainingPlans = results
      this.numberOfTrainingPlans = totalCount
    } finally {
      this.isFetchingList = false
    }
  }

  async createTrainingPlan(dto: ICreateTrainingPlanDto, orgId?: number): Promise<ITrainingPlan> {
    const result = await createTrainingPlan(dto, orgId)
    return result
  }

  async updateTrainingPlanStatus(id: number, status: number): Promise<void> {
    await updateTrainingPlanById(id, { status })
  }

  async archiveMultipleTrainingPlans(ids: number[]): Promise<void> {
    await archiveMultipleTrainingPlans(ids)
  }

  async restoreMultipleTrainingPlans(ids: number[]): Promise<void> {
    await restoreMultipleTrainingPlans(ids)
  }

  setManageModeInList(value: boolean): void {
    this.isManageModeInList = value
  }

  async createVariant(id: number, name: string): Promise<{ message: string; data: { id: number } }> {
    this.isCreatingVariant = true
    try {
      return await createTrainingPlanVariant(id, name)
    } finally {
      this.isCreatingVariant = false
    }
  }

  async duplicate(id: number, name: string): Promise<{ message: string; data: { id: number } }> {
    this.isDuplicatingPlan = true
    try {
      return await duplicateTrainingPlan(id, name)
    } finally {
      this.isDuplicatingPlan = false
    }
  }

  /**
   * Clear org-scoped training-plan list state on active-profile switch so a
   * switched profile never inherits another profile's cached plans.
   */
  reset(): void {
    this.trainingPlans = []
    this.numberOfTrainingPlans = 0
    this.isFetchingList = false
    this.isManageModeInList = false
    this.isCreatingVariant = false
    this.isDuplicatingPlan = false
  }
}

export default TrainingPlanStore
